import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import classes from "./CarDetails.module.css";
import CommonCard from "../../components/ui/CommonCard/CommonCard";
import Table from "../../components/Table/Table";
import { env } from "../../constants/env";

const FeatureGroup = ({ title, features }) => (
  <CommonCard className={classes.customerCard}>
    <h5 className={classes.title}>{title}</h5>
    <div className={classes.grid}>
      {features.map((feature) => (
        <label key={feature} className={classes.checkboxLabel}>
          <input type="checkbox" checked readOnly />
          <span>{feature}</span>
        </label>
      ))}
    </div>
  </CommonCard>
);

const CarDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const carId = id;

  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showTableDialog, setShowTableDialog] = useState(false);
  const [activeTab, setActiveTab] = useState("Overview");

  const [car, setCar] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState(null);

  const overviewRef = useRef();
  const featuresRef = useRef();
  const imagesRef = useRef();
  const bookingsRef = useRef();
  const reviewsRef = useRef();

  const sectionRefs = [
    { label: "Overview", ref: overviewRef },
    { label: "Features", ref: featuresRef },
    { label: "Images", ref: imagesRef },
    { label: "Bookings", ref: bookingsRef },
    { label: "Reviews", ref: reviewsRef },
  ];

  const scrollToRef = (ref) => {
    if (ref.current) {
      ref.current.style.scrollMarginTop = '90px';
      ref.current.style.scrollBehavior = 'smooth';
      ref.current.scrollIntoView({ block: 'start' });
      setTimeout(() => {
        ref.current.style.scrollMarginTop = '';
      }, 1000);
    }
  };

  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: "0px",
      threshold: 0.3,
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const matching = sectionRefs.find((s) => s.ref.current === entry.target);
          if (matching) setActiveTab(matching.label);
        }
      });
    }, observerOptions);

    sectionRefs.forEach(({ ref }) => {
      if (ref.current) observer.observe(ref.current);
    });

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const fetchDetails = async () => {
      try {
        const { data } = await axios.get(`${env.apiUrl}/api/cars/${carId}/details`);
        console.log("CAR DATA", data)
        setCar(data.car);
        setBookings(data.bookings);
        setReviews(data.reviews);
        setStats(data.stats);
      } catch (err) {
        console.error("Failed to fetch car details", err);
      }
    };
    fetchDetails();
  }, [carId]);

  const columns = [
    // { key: "id", label: "ID" },
    { key: "customer", label: "CUSTOMER" },
    { key: "car", label: "CAR" },
    { key: "date", label: "DATE" },
    { key: "status", label: "STATUS" },
    { key: "amount", label: "AMOUNT" },
  ];

  if (!car || !stats) return <p>Loading...</p>;

  const comfortFeatures = Object.entries(car.features?.comfortAndConvenience || {})
    .filter(([, val]) => val)
    .map(([key]) => key.replace(/([A-Z])/g, ' $1').trim());

  const safetyFeatures = Object.entries(car.features?.safety || {})
    .filter(([, val]) => val)
    .map(([key]) => key.replace(/([A-Z])/g, ' $1').trim());

  const carInfo = [
    { name: "Make & Model", value: `${car.make} ${car.model} ${car.year}` },
    { name: "Color", value: car.color },
    { name: "Current mileage", value: car.currentMileage?.toLocaleString() },
    { name: "Category", value: car.category },
    { name: "Type", value: car.type },
    { name: "Transmission", value: car.transmission },
    { name: "Number of seats", value: car.numberOfSeats },
    { name: "Engine size", value: car.engineSize },
  ];

  const pricingInfo = [
    { name: "Local hire rate", value: `K${car.localDailyRate}` },
    { name: "Outside city hire rate", value: `K${car.outsideCityDailyRate}` },
    { name: "Average revenue per day", value: stats.totalRevenue },
  ];

  return (
    <div className={classes.container}>
      <div className={`${classes.tabs} d-flex`}>
        {sectionRefs.map(({ label, ref }) => (
          <button
            key={label}
            className={`${classes.tab} ${activeTab === label ? classes.active : ""}`}
            onClick={() => scrollToRef(ref)}
          >
            {label}
          </button>
        ))}
      </div>

      <div ref={overviewRef}>
        <div className={classes.statsContainer}>
          <CommonCard className={classes.statCard}>
            <h3>{stats.totalBookings}</h3>
            <p>Total bookings this year</p>
          </CommonCard>
          <CommonCard className={classes.statCard}>
            <h3>{stats.totalRevenue}</h3>
            <p>Total revenue this year</p>
          </CommonCard>
          <CommonCard className={classes.statCard}>
            <h3>{stats.avgRating}</h3>
            <p>Average rating</p>
          </CommonCard>
          {/* <CommonCard className={classes.statCard}>
            <h3>{stats.utilization}</h3>
            <p>Utilization rate last 30 days</p>
          </CommonCard> */}
        </div>

        <div className="row">
          <div className="col-md-6">
            <CommonCard className={classes.customerCard}>
              <h3>Car info</h3>
              <div className={`d-flex flex-column ${classes.customerMetrics}`}>
                {carInfo.map((item, i) => (
                  <div className="d-flex align-items-center justify-content-between" key={i}>
                    <h4>{item.name}</h4>
                    <p>{item.value}</p>
                  </div>
                ))}
              </div>
            </CommonCard>
          </div>
          <div className="col-md-6 d-flex flex-column gap-4">
            <CommonCard className={`${classes.customerCard} ${classes.halfCard}`}>
              <h3>Status & location</h3>
              <div className={`d-flex flex-column ${classes.customerMetrics}`}>
                <div className="d-flex align-items-center justify-content-between">
                  <h4>Availability status</h4>
                  <p>{car.availabilityStatus}</p>
                </div>
              </div>
            </CommonCard>
            <CommonCard className={`${classes.customerCard} ${classes.halfCard}`}>
              <h3>Pricing</h3>
              <div className={`d-flex flex-column ${classes.customerMetrics}`}>
                {pricingInfo.map((item, i) => (
                  <div className="d-flex align-items-center justify-content-between" key={i}>
                    <h4>{item.name}</h4>
                    <p>{item.value}</p>
                  </div>
                ))}
              </div>
            </CommonCard>
          </div>
        </div>
      </div>

      <div ref={featuresRef} className={classes.section}>
        <h4>Features</h4>
        <div className="row">
          <div className="col-md-6">
            <FeatureGroup title="Comfort & convenience" features={comfortFeatures} />
          </div>
          <div className="col-md-6">
            <FeatureGroup title="Safety" features={safetyFeatures} />
          </div>
        </div>
      </div>

      <div ref={imagesRef} className={classes.section}>
        <h4>Images & description</h4>
        <CommonCard className={classes.customerCard}>
          <div className="row">
            {(car.images || []).map((image, i) => (
              <div className="col-md-4 mt-3" key={i}>
                <div className={classes.carImageContainer} style={{ backgroundImage: `url(${image})` }} />
              </div>
            ))}
          </div>
        </CommonCard>
        <CommonCard className={`${classes.customerCard} ${classes.descriptionContainer} mt-4`}>
          <h5>Description</h5>
          <p>{car.description}</p>
        </CommonCard>
      </div>

      <div ref={bookingsRef} className={classes.section}>
        <h4>Bookings</h4>
        <CommonCard className={`mb-4`}>
          <Table
            dataType={"Bookings"}
            data={bookings}
            columns={columns}
            showDialog={showTableDialog}
            setShowDialog={setShowTableDialog}
            bookingInfo={() => navigate(`/bookings/bookings-management/${bookings[selectedBooking]?.id}`)}
            clickedIndex={setSelectedBooking}
          />
        </CommonCard>
      </div>

      <div ref={reviewsRef} className={classes.section}>
        <h4>Reviews</h4>
        <div className={`${classes.reviewsContainer} d-flex flex-column gap-4 mt-3`}>
          {reviews.map((r, i) => (
            <CommonCard className={classes.customerCard} key={i}>
              <div className="d-flex align-items-center justify-content-between">
                <p className={classes.reviewText}>{r.comment}</p>
                <p className={classes.dateText}>{r.date}</p>
              </div>
              <div className={`${classes.reviewFooter} d-flex align-items-center gap-2`}>
                <div className={classes.avatarContainer} style={{ backgroundImage: `url(${r.user.avatar})` }} />
                <div>
                  <strong className={classes.reviewAuthor}>{r.user.name}</strong>
                  <div className={classes.stars}>{"★".repeat(r.rating)}</div>
                </div>
              </div>
            </CommonCard>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CarDetails;